import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Edit, Trash2 } from 'lucide-react'
import { promoCodesService } from '../../services/api'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import LoadingSpinner from '../LoadingSpinner'

const PromoCodesTab = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isDeleting, setIsDeleting] = useState(false)
  
  // Direct data fetching independent from parent
  const { 
    data: promoCodes = [], 
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['promo-codes-direct'],
    queryFn: async () => {
      const result = await promoCodesService.getPromoCodes()
      return result
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => promoCodesService.deletePromoCode(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['promo-codes-direct'])
      setIsDeleting(false)
    },
    onError: (error) => {
      console.error('Delete error:', error)
      setIsDeleting(false)
      alert('حدث خطأ أثناء حذف كوبون الخصم')
    }
  })

  const handleDelete = (id) => {
    if (window.confirm('هل أنت متأكد من رغبتك في حذف كوبون الخصم هذا؟')) {
      setIsDeleting(true)
      deleteMutation.mutate(id)
    }
  }

  const handleAddPromoCode = () => {
    navigate('/admin/promocode/new')
  }

  const handleEditPromoCode = (promo) => {
    navigate(`/admin/promocode/edit/${promo.id}`)
  }

  // Check if we have data right after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (promoCodes.length === 0 && !isLoading) {
        refetch()
      }
    }, 500)
    
    return () => clearTimeout(timer)
  }, [promoCodes.length, isLoading, refetch])

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <LoadingSpinner size="lg" />
        <span className="ml-2">جاري تحميل أكواد الخصم...</span>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
        <p>خطأ في تحميل كوبونات الخصم: {error?.message || 'حدث خطأ غير معروف'}</p>
        <button 
          onClick={() => refetch()}
          className="mt-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg"
        >
          إعادة المحاولة
        </button>
      </div>
    )
  }

  // Empty state
  if (!promoCodes || promoCodes.length === 0) {
    return (
      <div className="text-center p-8">
        <div className="inline-flex justify-center items-center p-8 bg-gray-50 rounded-full mb-4">
          <span className="text-4xl text-gray-400">%</span>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد أكواد خصم بعد</h3>
        <p className="text-gray-500 mb-6">إنشاء رموز ترويجية لتقديم خصومات لعملائك.</p>
        <button 
          onClick={handleAddPromoCode}
          className="inline-flex items-center px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800"
        >
          <Plus className="w-4 h-4 mr-2" /> إنشاء رمزك الترويجي الأول
        </button>
      </div>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">إدارة أكواد الخصم</h2>
        <button 
          onClick={handleAddPromoCode}
          className="flex items-center px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800"
        >
          <Plus className="w-4 h-4 mr-2" /> إضافة رمز ترويجي
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Description</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Type</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Value</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Expiry</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Status</th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {promoCodes.map((promo) => (
              <tr key={promo.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {promo.code}
                  {/* Mobile-only additional info - simplified */}
                  <div className="md:hidden text-xs text-gray-500 mt-1">
                    <div>{promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `${promo.discount_value} KD`}</div>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${promo.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {promo.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">{promo.description || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">{promo.discount_type}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                  {promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `${promo.discount_value} KD`}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                  {promo.valid_until ? new Date(promo.valid_until).toLocaleDateString() : 'No expiry'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${promo.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {promo.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex flex-col space-y-1 md:flex-row md:justify-end md:space-y-0 md:space-x-2">
                    <button 
                      onClick={() => handleEditPromoCode(promo)}
                      className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 flex items-center justify-center"
                      title="Edit Promo Code"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(promo.id)}
                      className="text-red-600 hover:text-red-900 disabled:opacity-50 p-1 rounded hover:bg-red-50 flex items-center justify-center"
                      disabled={isDeleting}
                      title="Delete Promo Code"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default PromoCodesTab
